import { inject, Injectable } from '@angular/core';
import { take } from 'rxjs';

import { PortalQueryParams } from '@app/app-routing.module';
import {
  CHECKOUT_BUNDLE_RESTORE_QUERY_VALUE,
  CHECKOUT_BASIC_TIER_QUERY_VALUE,
} from '@app/utils/checkout-resume.util';
import { State } from '@app/services/state/state.service';
import { AuthService } from '@app/services/auth/auth.service';
import { ErrorService } from '@app/services/error.service';
import { SignInModalService } from '@app/services/sign-in-modal.service';
import { handleError } from '@app/operators/handle-error';
import { Tracking } from '@app/services/tracking/tracking.service';
import { PortalTrackingEvents } from '@app/utils/tracking-events';

/** Query-string snapshot so post-login redirect can restore tier / ancillary selections. */
export type PortalSignInRedirectExtras = {
  tier?: string;
  disruptionProtection?: number;
  cancelForAnyReason?: number;
  restoreBundleSelection?: boolean;
  /** Third route segment to open after auth (e.g. `travelers`, `disruption-protection`). */
  checkoutResume?: string;
  /** Default `option`: `/:lead/:option/:checkoutResume`. Use `upsell` for upgrade flow. */
  checkoutPath?: 'option' | 'upsell';
  /** Required for `checkoutPath === 'upsell'` (param in `/:upgrade/:upsellTrip/:segment`). */
  upsellTripPortalLink?: string;
};

export function checkoutExtrasFromState(state: State): PortalSignInRedirectExtras | undefined {
  const tier = state.basicTierSelected
    ? CHECKOUT_BASIC_TIER_QUERY_VALUE
    : state.tier != null
      ? String(state.tier)
      : undefined;
  const disruptionProtection =
    state.disruptionProtection != null && state.disruptionProtection > 0
      ? state.disruptionProtection
      : undefined;
  const cancelForAnyReason =
    state.cancelForAnyReason != null && state.cancelForAnyReason > 0
      ? state.cancelForAnyReason
      : undefined;
  const restoreBundleSelection = Boolean(state.bundle);

  if (
    !tier &&
    disruptionProtection == null &&
    cancelForAnyReason == null &&
    !restoreBundleSelection
  ) {
    return undefined;
  }

  return {
    ...(tier ? { tier } : {}),
    ...(disruptionProtection != null ? { disruptionProtection } : {}),
    ...(cancelForAnyReason != null ? { cancelForAnyReason } : {}),
    ...(restoreBundleSelection ? { restoreBundleSelection: true } : {}),
  };
}

export function buildPortalSignInRedirectUrl(
  leadPortalLink: string,
  optionPortalLink: string,
  extras?: PortalSignInRedirectExtras,
): string {
  const base = window.location.href.split('/').slice(0, 4).join('/');
  const params = new URLSearchParams();

  params.set('portalLink', leadPortalLink);
  params.set('option', optionPortalLink);

  if (extras?.tier) {
    params.set('tier', extras.tier);
  }

  if (extras?.disruptionProtection != null && extras.disruptionProtection > 0) {
    params.set('disruptionProtection', String(extras.disruptionProtection));
  }

  if (extras?.cancelForAnyReason != null && extras.cancelForAnyReason > 0) {
    params.set('cancelForAnyReason', String(extras.cancelForAnyReason));
  }

  if (extras?.restoreBundleSelection) {
    params.set('bundle', CHECKOUT_BUNDLE_RESTORE_QUERY_VALUE);
  }

  if (extras?.checkoutResume) {
    params.set(PortalQueryParams.CheckoutResume, extras.checkoutResume);
  }

  if (extras?.checkoutPath === 'upsell') {
    params.set(PortalQueryParams.CheckoutPath, 'upsell');

    if (extras.upsellTripPortalLink) {
      params.set(PortalQueryParams.UpsellTripPortalLink, extras.upsellTripPortalLink);
    }
  } else if (extras?.checkoutResume) {
    params.set(PortalQueryParams.CheckoutPath, 'option');
  }

  return `${base}/?${params.toString()}`;
}

/**
 * Per-component instance (provide on the host @Component). Coordinates the ask-to-sign-in modal
 * resume flow. Use `runAfterSessionCheckIf` so the modal appears only when the next navigation
 * targets travelers (`PortalRoutes.Travelers`); pass `gateSignIn: false` to proceed without it.
 */
@Injectable()
export class SessionProceedCoordinator {
  pendingAfterAuth: (() => void) | null = null;

  authService = inject(AuthService);
  errorService = inject(ErrorService);
  signInModalService = inject(SignInModalService);

  runAfterSessionCheck(isNewUser: boolean, action: () => void, showAskModal: () => void) {
    this.authService
      .hasSession()
      .pipe(take(1), handleError(this.errorService))
      .subscribe((hasSession) => {
        if (hasSession || isNewUser) {
          action();
        } else {
          this.pendingAfterAuth = action;
          showAskModal();
        }
      });
  }

  /** When `gateSignIn` is false, runs `action` immediately (no ask-to-sign-in modal). */
  runAfterSessionCheckIf(
    gateSignIn: boolean,
    isNewUser: boolean,
    action: () => void,
    showAskModal: () => void,
  ) {
    if (!gateSignIn) {
      action();
      return;
    }

    this.runAfterSessionCheck(isNewUser, action, showAskModal);
  }

  trackSignInFromCheckoutPromptOpened() {
    Tracking.track(PortalTrackingEvents.portalSignInFromSelectOptionPressed);
  }

  openSignInModalForPortalOption(
    leadPortalLink: string,
    optionPortalLink: string,
    extras?: PortalSignInRedirectExtras,
  ) {
    const redirectUrl = buildPortalSignInRedirectUrl(leadPortalLink, optionPortalLink, extras);

    this.signInModalService.showModal(false, redirectUrl, leadPortalLink);
  }

  continueAsGuestAfterPrompt(hideModal: () => void) {
    const fn = this.pendingAfterAuth;
    this.pendingAfterAuth = null;
    hideModal();
    fn?.();
  }

  dismissAskModal(hideModal: () => void) {
    this.pendingAfterAuth = null;
    hideModal();
  }
}
